import {createContext, PropsWithChildren, ReactNode, useContext, useEffect, useState} from 'react';
import {useMatches} from 'react-router-dom';

interface Breadcrumb {
  label: string;
  path: string;
}

interface BreadcrumbOverride {
  label?: string;
  href?: string;
}

interface PageTitleContextValue {
  title: string | null;
  setTitle: (title: string | null) => void;
  headerContent: ReactNode | null;
  setHeaderContent: (content: ReactNode | null) => void;
  breadcrumbOverrides: Record<string, BreadcrumbOverride>;
  setBreadcrumbOverride: (path: string, override: BreadcrumbOverride) => void;
  removeBreadcrumbOverride: (path: string) => void;
}

const PageTitleContext = createContext<PageTitleContextValue>({
  title: null,
  setTitle: () => {},
  headerContent: null,
  setHeaderContent: () => {},
  breadcrumbOverrides: {},
  setBreadcrumbOverride: () => {},
  removeBreadcrumbOverride: () => {},
});

export function PageTitleProvider({ children }: PropsWithChildren) {
  const [title, setTitle] = useState<string | null>(null);
  const [headerContent, setHeaderContent] = useState<ReactNode | null>(null);
  const [breadcrumbOverrides, setBreadcrumbOverrides] = useState<Record<string, BreadcrumbOverride>>({});

  const setBreadcrumbOverride = (path: string, override: BreadcrumbOverride) => {
    setBreadcrumbOverrides(prev => ({ ...prev, [path]: override }));
  };

  const removeBreadcrumbOverride = (path: string) => {
    setBreadcrumbOverrides(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  return (
    <PageTitleContext.Provider
      value={{
        title,
        setTitle,
        headerContent,
        setHeaderContent,
        breadcrumbOverrides,
        setBreadcrumbOverride,
        removeBreadcrumbOverride
      }}
    >
      {children}
    </PageTitleContext.Provider>
  );
}

export function usePageTitle(dynamicTitle?: string) {
  const { title, setTitle } = useContext(PageTitleContext);

  useEffect(() => {
    if (dynamicTitle) setTitle(dynamicTitle);
    return () => setTitle(null);
  }, [dynamicTitle]);

  return title;
}

export function useBreadcrumbTitle(path: string, title?: string, href?: string) {
  const { setBreadcrumbOverride, removeBreadcrumbOverride } = useContext(PageTitleContext);

  useEffect(() => {
    if (title || href) setBreadcrumbOverride(path, { label: title, href });
    return () => removeBreadcrumbOverride(path);
  }, [path, title, href]);
}

export function usePageHeaderContent(content?: ReactNode) {
  const { headerContent, setHeaderContent } = useContext(PageTitleContext);

  useEffect(() => {
    if (content) setHeaderContent(content);
    return () => setHeaderContent(null);
  }, [content]);

  return headerContent;
}

export function useBreadcrumbs(): Breadcrumb[] {
  const matches = useMatches();
  const dynamicTitle = usePageTitle();
  const { breadcrumbOverrides } = useContext(PageTitleContext);

  const breadcrumbs = matches
    .filter((match) => (match.handle as { title?: string })?.title)
    .map((match) => {
      const override = breadcrumbOverrides[match.pathname];
      return {
        label: override?.label ?? (match.handle as { title: string }).title,
        path: override?.href ?? match.pathname,
      };
    });

  if (breadcrumbs.length > 0 && dynamicTitle) {
    breadcrumbs[breadcrumbs.length - 1].label = dynamicTitle;
  }

  return breadcrumbs;
}
